import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../../styles/theme';

interface AiTokenProps {
  label: string;
  kind: string;
  neg?: boolean;
  /**
   * Who created this filter. Purple + zap is reserved for the model's own
   * inferences — a word the user typed must not be presented as one.
   */
  source?: 'user' | 'ai';
  onRemove: () => void;
}

const KIND_ICON: Record<string, string> = {
  time: 'clock',
  diet: 'circle',
  rate: 'star',
  sort: 'shuffle',
  pantry: 'box',
  // The planner's Library pre-applies the slot's meal ("Déjeuner").
  meal: 'sunrise',
};

/**
 * Ingredient tokens have no kind-specific icon, so they fall back by
 * provenance: a tag for the user's own word, the zap for a concept the model
 * inferred. Previously everything fell through to 'zap', which badged every
 * word the user typed as an AI inference.
 */
const FALLBACK_ICON = { user: 'tag', ai: 'zap' } as const;

/** Exclusions read as exclusions regardless of who added them. */
const NEG_ICON = 'minus-circle';

const PALETTE = {
  user: { bg: Paprika.accentLight, border: Paprika.brandSoft, text: Paprika.primary },
  ai: { bg: Paprika.aiSoft, border: Paprika.aiDots, text: Paprika.ai },
} as const;

export const AiToken: React.FC<AiTokenProps> = ({
  label,
  kind,
  neg,
  source = 'user',
  onRemove,
}) => {
  const palette = PALETTE[source];
  // Colour carries provenance, the icon carries polarity — the two stay
  // readable independently.
  const iconName = neg ? NEG_ICON : KIND_ICON[kind] ?? FALLBACK_ICON[source];

  return (
    <View style={[styles.chip, { backgroundColor: palette.bg, borderColor: palette.border }]}>
      <Feather name={iconName as any} size={12} color={palette.text} style={styles.icon} />
      <Text style={[styles.label, { color: palette.text }]}>{label}</Text>
      <TouchableOpacity onPress={onRemove} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <Feather name="x" size={13} color={palette.text} style={styles.closeIcon} />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    borderWidth: 1,
    paddingVertical: 6,
    paddingLeft: 10,
    paddingRight: 8,
    gap: 5,
  },
  icon: {
    flexShrink: 0,
  },
  label: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
  },
  closeIcon: {
    marginLeft: 2,
  },
});
